import { Dispatch, ReactElement, SetStateAction } from 'react';

import {
  FieldRequiredIndicator,
  IAddress,
  StyledSelect,
  TextField2,
} from '../common';
import { assocPath, compose, pathOr, map } from 'rambda';
import states from 'states-us';
import { Container, MenuItem, styled, useMediaQuery } from '@mui/material';

export function UpdateMailTo({
  addressFromDetails,
  setDetails,
}: {
  addressFromDetails: IAddress;
  setDetails: Dispatch<
    SetStateAction<{
      address: IAddress;
      phone: string | null;
    }>
  >;
}) {
  const setDetailsAt = (property: string) =>
    compose(setDetails, assocPath(property), pathOr('', 'target.value'));

  const matchesTabsBreakpoint = useMediaQuery('(min-width:80em)', {
    noSsr: true,
  });

  const value = (property: string) => pathOr('', property);

  const renderCityStateZipList = () => (
    <>
      <TextField2
        id={`city`}
        value={value('city')(addressFromDetails)}
        onChange={setDetailsAt('address.city')}
        variant="outlined"
      />

      <Name htmlFor={`state`}>
        State <FieldRequiredIndicator /> :
      </Name>

      <StyledSelect
        labelId="role"
        value={value('stateCd')(addressFromDetails)}
        onChange={setDetailsAt('address.stateCd')}
        variant="outlined">
        {map<{ name: string; abbreviation: string }, ReactElement>(
          ({ name, abbreviation }) => (
            <MenuItem value={abbreviation} key={abbreviation}>
              {name}
            </MenuItem>
          )
        )(states)}
      </StyledSelect>

      <Name htmlFor={`zip-code`}>
        ZIP Code <FieldRequiredIndicator /> :
      </Name>
      <TextField2
        id={`zip-code`}
        value={value('postalCd')(addressFromDetails)}
        onChange={setDetailsAt('address.postalCd')}
        variant="outlined"
      />
    </>
  );

  const renderCityStateZip = () => (
    <>
      <TextField2
        id={`city`}
        value={value('city')(addressFromDetails)}
        onChange={setDetailsAt('address.city')}
        variant="outlined"
      />
      <Pair data-testid="state-label-wrapper">
        <Name htmlFor={`state`}>
          State <FieldRequiredIndicator /> :
        </Name>

        <StyledSelect
          labelId="role"
          value={value('stateCd')(addressFromDetails)}
          onChange={setDetailsAt('address.stateCd')}
          variant="outlined">
          {map<{ name: string; abbreviation: string }, ReactElement>(
            ({ name, abbreviation }) => (
              <MenuItem value={abbreviation} key={abbreviation}>
                {name}
              </MenuItem>
            )
          )(states)}
        </StyledSelect>
      </Pair>
      <Pair>
        <Name htmlFor={`zip-code`}>
          ZIP Code <FieldRequiredIndicator /> :
        </Name>
        <TextField2
          id={`zip-code`}
          value={value('postalCd')(addressFromDetails)}
          onChange={setDetailsAt('address.postalCd')}
          variant="outlined"
        />
      </Pair>
    </>
  );

  return (
    <Container maxWidth="lg">
      <fieldset>
        <legend>Business Mailing Address</legend>
        <Wrapper>
          <Name htmlFor={`street1`}>
            Address <FieldRequiredIndicator /> :
          </Name>
          <TextField2
            id={`street1`}
            value={pathOr('', 'street1')(addressFromDetails)}
            onChange={setDetailsAt('address.street1')}
            variant="outlined"
          />
          <Name htmlFor={`street2`}>Suite / Room / Floor :</Name>
          <TextField2
            id={`street2`}
            value={pathOr('', 'street2')(addressFromDetails)}
            onChange={setDetailsAt('address.street2')}
            variant="outlined"
          />

          <Name htmlFor={`city`} aria-label="City">
            City <FieldRequiredIndicator /> :
          </Name>
          {matchesTabsBreakpoint ? (
            <CityStateZip>{renderCityStateZip()}</CityStateZip>
          ) : (
            renderCityStateZipList()
          )}
        </Wrapper>
      </fieldset>
    </Container>
  );
}

const Name = styled('label')(({ theme }) => ({ fontWeight: 700 }));

const Wrapper = styled('article')(({ theme }) => ({
  display: 'grid',
  gap: `1rem`,
  alignItems: 'center',

  gridTemplateColumns: '1fr',

  '& > label': {
    justifySelf: 'start',
  },

  '@media (min-width: 80em)': {
    gridTemplateColumns: '1fr 4fr',

    '& > label': {
      justifySelf: 'end',
    },
  },
}));

const CityStateZip = styled('span')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const Pair = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '&>*+*': {
    marginLeft: `1rem`,
  },
}));
